/**
 * Idle Manager Service
 * Detects user inactivity and triggers appropriate actions
 */

import { throttle } from '@/utils/throttle';
import { router } from '@/router';
import { validateSessionWithRefresh, isTokenExpiredClientSide } from '@/store/helpers/authenticate/tokens';
import { useUserStore } from '@/store/user.store';
import { useApplicationStore } from '@/store/application.store';
import { Auth } from '@/firebase/auth';
import { useCentralizedDemo } from '@/composables/useCentralizedDemo';
import { SESSION_EXPIRED_DEDUPE_KEY, SESSION_EXPIRED_DEDUPE_TTL } from '@/constants/notifications';

export interface IdleManagerOptions {
  idleTimeout?: number; // Time before considering user idle (ms)
  warningTimeout?: number; // Time before showing warning (ms)
  debounceDelay?: number; // Debounce delay for activity events (ms)
  enableInDevelopment?: boolean; // Enable in development mode
  sessionValidationInterval?: number; // Session validation check interval (ms)
  enableSessionValidation?: boolean; // Enable automatic session validation
  onSessionExpired?: () => void; // Callback when session expires
  onSessionRefreshed?: () => void; // Callback when session is refreshed
}

export interface IdleManagerDebugInfo {
  isIdle: boolean;
  isDestroyed: boolean;
  lastActivity: string;
  timeUntilIdle: string;
  timeUntilWarning: string;
  idleTimeout: string;
  warningTimeout: string;
  enableSessionValidation: boolean;
  lastSessionCheck: string;
  timeUntilSessionCheck: string;
  sessionValidationInterval: string;
}

export class IdleManager {
  private idleTimeout: number;
  private warningTimeout: number;
  private debounceDelay: number;
  private idleTimer?: NodeJS.Timeout;
  private warningTimer?: NodeJS.Timeout;
  private lastActivity: number = Date.now();
  private isIdle: boolean = false;
  private isDestroyed: boolean = false;
  private activityHandler: () => void;
  private boundEventHandler: () => void;
  
  // Session validation properties
  private sessionValidationInterval: number;
  private enableSessionValidation: boolean;
  private sessionTimer?: NodeJS.Timeout;
  private lastSessionCheck: number = Date.now();
  private onSessionExpired?: () => void;
  private onSessionRefreshed?: () => void;
  
  // Activity events to monitor
  private readonly activityEvents = [
    'mousedown',
    'mousemove',
    'keypress',
    'keydown',
    'scroll',
    'touchstart',
    'touchmove',
    'click',
    'focus'
  ];

  constructor(
    private onIdle: () => void,
    private onWarning?: () => void,
    private onActivity?: () => void,
    options: IdleManagerOptions = {}
  ) {
    // If demo mode is active, create a no-op instance
    try {
      const { isDemoMode } = useCentralizedDemo();
      if (isDemoMode.value) {
        console.log('[IdleManager] Demo mode active - disabling idle/session monitoring');
        this.idleTimeout = 0 as any; // placeholder values; instance is no-op
        this.warningTimeout = 0 as any;
        this.debounceDelay = 0 as any;
        this.sessionValidationInterval = 0 as any;
        this.enableSessionValidation = false;
        this.activityHandler = () => {};
        this.boundEventHandler = () => {};
        this.isDestroyed = true; // mark as inactive
        return;
      }
    } catch (e) {
      // If demo detection fails, continue normally
    }
    // Configure timeouts
    this.idleTimeout = options.idleTimeout || 15 * 60 * 1000; // 15 minutes default
    this.warningTimeout = options.warningTimeout || 13 * 60 * 1000; // 13 minutes default
    this.debounceDelay = options.debounceDelay || 1000; // 1 second default
    
    // Configure session validation (increased to 12 minutes to reduce server calls)
    this.sessionValidationInterval = options.sessionValidationInterval || 12 * 60 * 1000; // 12 minutes default
    this.enableSessionValidation = options.enableSessionValidation ?? true; // Enabled by default
    this.onSessionExpired = options.onSessionExpired;
    this.onSessionRefreshed = options.onSessionRefreshed;
    
    // Check if should be enabled
    const isDevelopment = import.meta.env.DEV;
    const enableInDev = options.enableInDevelopment ?? false;
    
    if (isDevelopment && !enableInDev) {
      console.log('[IdleManager] Disabled in development mode');
      // Create no-op activity handler
      this.activityHandler = () => {};
      this.boundEventHandler = () => {};
      return;
    }
    
    // Create throttled activity handler
    this.activityHandler = throttle(() => {
      this.handleUserActivity();
    }, this.debounceDelay);
    
    // Bind to preserve context
    this.boundEventHandler = this.activityHandler.bind(this);
    
    // Set up listeners and start monitoring
    this.setupListeners();
    this.resetTimers();
    
    // Start session validation if enabled
    if (this.enableSessionValidation && !isDevelopment) {
      this.startSessionValidation();
    }
    
    console.log('[IdleManager] Initialized with timeouts:', {
      idle: this.idleTimeout / 1000 / 60 + ' minutes',
      warning: this.warningTimeout / 1000 / 60 + ' minutes',
      sessionValidation: this.enableSessionValidation ? this.sessionValidationInterval / 1000 / 60 + ' minutes' : 'disabled'
    });
  }

  /**
   * Set up event listeners for user activity
   */
  private setupListeners(): void {
    // Monitor DOM events
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.boundEventHandler, { passive: true });
    });
    
    // Monitor router navigation
    if (router) {
      router.afterEach(() => {
        this.handleUserActivity();
      });
    }
    
    // Monitor visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.handleUserActivity();
      }
    });
    
    // Monitor window focus
    window.addEventListener('focus', this.boundEventHandler);
  }

  /**
   * Handle user activity
   */
  private handleUserActivity(): void {
    if (this.isDestroyed) return;
    
    const now = Date.now();
    const wasIdle = this.isIdle;
    
    this.lastActivity = now;
    this.isIdle = false;
    
    // Reset timers on any activity
    this.resetTimers();
    
    // If recovering from idle, trigger callback
    if (wasIdle && this.onActivity) {
      console.log('[IdleManager] User activity detected after idle period');
      this.onActivity();
    }
  }

  /**
   * Reset idle and warning timers
   */
  public resetTimers(): void {
    if (this.isDestroyed) return;
    
    // Clear existing timers
    this.clearTimers();
    
    // Set warning timer (if callback provided)
    if (this.onWarning) {
      this.warningTimer = setTimeout(() => {
        if (!this.isDestroyed && !this.isIdle && this.onWarning) {
          console.log('[IdleManager] Idle warning triggered');
          this.onWarning();
        }
      }, this.warningTimeout);
    }
    
    // Set idle timer
    this.idleTimer = setTimeout(() => {
      if (!this.isDestroyed) {
        console.log('[IdleManager] User is now idle');
        this.isIdle = true;
        this.onIdle();
      }
    }, this.idleTimeout);
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = undefined;
    }
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = undefined;
    }
  }

  /**
   * Get time until idle (in milliseconds)
   */
  public getTimeUntilIdle(): number {
    const elapsed = Date.now() - this.lastActivity;
    const remaining = this.idleTimeout - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Get time until warning (in milliseconds)
   */
  public getTimeUntilWarning(): number {
    const elapsed = Date.now() - this.lastActivity;
    const remaining = this.warningTimeout - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Check if currently idle
   */
  public getIsIdle(): boolean {
    return this.isIdle;
  }

  /**
   * Force idle state (for testing)
   */
  public forceIdle(): void {
    if (this.isDestroyed) return;
    
    console.log('[IdleManager] Forcing idle state');
    this.clearTimers();
    this.isIdle = true;
    this.onIdle();
  }

  /**
   * Update idle timeout
   */
  public setIdleTimeout(timeout: number): void {
    this.idleTimeout = timeout;
    this.warningTimeout = Math.max(0, timeout - 2 * 60 * 1000); // 2 minutes before idle
    this.resetTimers();
  }

  /**
   * Start session validation monitoring
   */
  private startSessionValidation(): void {
    if (this.isDestroyed || !this.enableSessionValidation) return;
    
    this.clearSessionTimer();
    this.sessionTimer = setInterval(async () => {
      if (!this.isDestroyed) {
        await this.validateSession();
      }
    }, this.sessionValidationInterval);
    
    console.log('[IdleManager] Session validation started');
  }

  /**
   * Stop session validation monitoring
   */
  private clearSessionTimer(): void {
    if (this.sessionTimer) {
      clearInterval(this.sessionTimer);
      this.sessionTimer = undefined;
    }
  }

  /**
   * Validate current session
   */
  private async validateSession(): Promise<void> {
    try {
      this.lastSessionCheck = Date.now();
      
      // Check if user is authenticated
      if (!Auth.currentUser) {
        console.log('[IdleManager] No authenticated user during session validation');
        return;
      }

      // Get current session token
      const descopeToken = localStorage.getItem('descope_session');
      if (!descopeToken) {
        console.warn('[IdleManager] No Descope session token found');
        this.handleSessionExpired();
        return;
      }

      // Quick client-side expiry check first
      if (isTokenExpiredClientSide(descopeToken)) {
        console.log('[IdleManager] Token appears expired client-side, attempting refresh...');
      }

      // Use enhanced validation with automatic refresh (no force, use cache if available)
      const validationResult = await validateSessionWithRefresh(false);
      if (!validationResult.valid) {
        console.warn('[IdleManager] Session validation failed:', validationResult.error);
        this.handleSessionExpired();
        return;
      }

      // Check if session was refreshed
      if (validationResult.refreshed) {
        console.log('[IdleManager] Session token was refreshed during validation');
      }

      // Check Firebase token health
      try {
        await Auth.currentUser.getIdToken(false);
        console.log('[IdleManager] Session validation successful');
        
        if (this.onSessionRefreshed) {
          this.onSessionRefreshed();
        }
      } catch (tokenError) {
        console.warn('[IdleManager] Firebase token error:', tokenError);
        await this.attemptFirebaseTokenRefresh();
      }
      
    } catch (error) {
      console.error('[IdleManager] Session validation error:', error);
      // Don't immediately expire on validation errors to avoid false positives
      // The session will be checked again on the next interval
    }
  }

  /**
   * Attempt to refresh Firebase tokens
   */
  private async attemptFirebaseTokenRefresh(): Promise<void> {
    try {
      if (!Auth.currentUser) {
        this.handleSessionExpired();
        return;
      }

      // Force refresh Firebase token
      await Auth.currentUser.getIdToken(true);
      
      // Refresh custom claims if available
      const userStore = useUserStore();
      if (userStore.refreshCustomClaims) {
        await userStore.refreshCustomClaims();
      }
      
      console.log('[IdleManager] Firebase token refresh successful');
      
      if (this.onSessionRefreshed) {
        this.onSessionRefreshed();
      }
      
    } catch (error) {
      console.error('[IdleManager] Firebase token refresh failed:', error);
      this.handleSessionExpired();
    }
  }

  /**
   * Handle session expiration
   */
  private async handleSessionExpired(): Promise<void> {
    console.error('[IdleManager] Session expired, initiating proper logout');
    
    try {
      // Suppress in demo mode
      try {
        const { isDemoMode } = useCentralizedDemo();
        if (isDemoMode.value) {
          console.log('[IdleManager] Demo mode - suppressing session expired notification/logout');
          return;
        }
      } catch {}

      // Import and call proper logout function
      const { useAuthenticateStore } = await import('@/store/authenticate.store');
      const authenticateStore = useAuthenticateStore();
      
      const applicationStore = useApplicationStore();
      
      // Get current route for context-aware message
      const { router } = await import('@/router');
      const currentRoute = router.currentRoute.value;
      const isOnLoginPage = currentRoute?.name === 'login' || currentRoute?.path === '/login';
      
      applicationStore.showNotification({
        title: 'Session Expired',
        message: isOnLoginPage 
          ? 'Your session has expired. Please log in again.'
          : 'Your session has expired due to inactivity. You will be redirected to login.',
        theme: 'warning',
        autoClose: false,
        persistent: true,
        isOpen: true,
        dedupeKey: SESSION_EXPIRED_DEDUPE_KEY,
        dedupeWindowMs: SESSION_EXPIRED_DEDUPE_TTL
      });
      
      // Call proper logout function
      await authenticateStore.logout();
      
      if (this.onSessionExpired) {
        this.onSessionExpired();
      }
      
    } catch (error) {
      console.error('[IdleManager] Error during session expiration logout:', error);
      
      // Fallback: show notification and attempt robust redirect to login
      const applicationStore = useApplicationStore();
      applicationStore.showNotification({
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again.',
        theme: 'warning',
        autoClose: false,
        persistent: true,
        isOpen: true,
        dedupeKey: SESSION_EXPIRED_DEDUPE_KEY,
        dedupeWindowMs: SESSION_EXPIRED_DEDUPE_TTL
      });

      // Attempt navigation fallbacks
      try {
        const { navigateWithLoadingState } = await import('@/utils/navigation');
        const { NAME_LOGIN } = await import('@/router/routes/constants');
        await navigateWithLoadingState({ name: NAME_LOGIN });
      } catch (navErr) {
        console.warn('[IdleManager] navigateWithLoadingState failed in catch, attempting router fallback:', navErr);
        try {
          const { router } = await import('@/router');
          await router.push('/login');
        } catch (routerErr) {
          console.error('[IdleManager] router.push fallback failed, forcing hard redirect:', routerErr);
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
      }
      
      if (this.onSessionExpired) {
        this.onSessionExpired();
      }
    }
  }

  /**
   * Temporarily extend the idle timeout
   */
  public extendTimeout(additionalTime: number): void {
    if (this.isDestroyed || this.isIdle) return;
    
    console.log(`[IdleManager] Extending timeout by ${additionalTime / 1000} seconds`);
    this.lastActivity = Date.now(); // Reset activity time
    this.resetTimers();
  }

  /**
   * Pause idle detection
   */
  public pause(): void {
    console.log('[IdleManager] Pausing idle detection');
    this.clearTimers();
  }

  /**
   * Resume idle detection
   */
  public resume(): void {
    if (this.isDestroyed) return;
    
    console.log('[IdleManager] Resuming idle detection');
    this.lastActivity = Date.now();
    this.isIdle = false;
    this.resetTimers();
  }

  /**
   * Clean up and destroy the idle manager
   */
  public destroy(): void {
    console.log('[IdleManager] Destroying idle manager');
    this.isDestroyed = true;
    
    // Clear all timers
    this.clearTimers();
    this.clearSessionTimer();
    
    // Remove event listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.boundEventHandler);
    });
    
    window.removeEventListener('focus', this.boundEventHandler);
    document.removeEventListener('visibilitychange', this.handleUserActivity);
  }

  /**
   * Force session validation check
   */
  public async forceSessionCheck(): Promise<void> {
    if (this.enableSessionValidation) {
      await this.validateSession();
    }
  }

  /**
   * Enable session validation
   */
  public enableSessionCheck(): void {
    this.enableSessionValidation = true;
    this.startSessionValidation();
  }

  /**
   * Disable session validation
   */
  public disableSessionCheck(): void {
    this.enableSessionValidation = false;
    this.clearSessionTimer();
  }

  /**
   * Get time until next session check
   */
  public getTimeUntilSessionCheck(): number {
    if (!this.enableSessionValidation || !this.sessionTimer) return 0;
    const elapsed = Date.now() - this.lastSessionCheck;
    const remaining = this.sessionValidationInterval - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Get debug information
   */
  public getDebugInfo(): IdleManagerDebugInfo {
    return {
      isIdle: this.isIdle,
      isDestroyed: this.isDestroyed,
      lastActivity: new Date(this.lastActivity).toLocaleTimeString(),
      timeUntilIdle: Math.round(this.getTimeUntilIdle() / 1000) + ' seconds',
      timeUntilWarning: Math.round(this.getTimeUntilWarning() / 1000) + ' seconds',
      idleTimeout: this.idleTimeout / 1000 / 60 + ' minutes',
      warningTimeout: this.warningTimeout / 1000 / 60 + ' minutes',
      // Session validation info
      enableSessionValidation: this.enableSessionValidation,
      lastSessionCheck: new Date(this.lastSessionCheck).toLocaleTimeString(),
      timeUntilSessionCheck: Math.round(this.getTimeUntilSessionCheck() / 1000) + ' seconds',
      sessionValidationInterval: this.sessionValidationInterval / 1000 / 60 + ' minutes'
    };
  }
}

// Singleton instance for global idle management
let globalIdleManager: IdleManager | null = null;

/**
 * Get or create global idle manager instance
 */
export function getGlobalIdleManager(): IdleManager | null {
  return globalIdleManager;
}

/**
 * Initialize global idle manager
 */
export function initializeGlobalIdleManager(
  onIdle: () => void,
  onWarning?: () => void,
  onActivity?: () => void,
  options?: IdleManagerOptions
): IdleManager {
  if (globalIdleManager) {
    globalIdleManager.destroy();
  }
  
  globalIdleManager = new IdleManager(onIdle, onWarning, onActivity, options);
  
  // Make available for debugging in development
  if (import.meta.env.DEV) {
    (window as any).idleManager = globalIdleManager;
  }
  
  return globalIdleManager;
}

/**
 * Destroy global idle manager
 */
export function destroyGlobalIdleManager(): void {
  if (globalIdleManager) {
    globalIdleManager.destroy();
    globalIdleManager = null;
  }
}